import { createContext, useContext, useEffect, useState } from "react";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [parent, setParent] = useState(null);
  const [childData, setChildData] = useState([]);
  const [learner, setLearner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [educatorData, setEducatorData] = useState(null);
  const [institutionData, setInstitutionData] = useState(null);

  const API_BASE = process.env.REACT_APP_API_BASE || "https://beintelli.engaggio.com/handlers";

  /* ================= LOGIN ================= */
  const login = async (identifier, password) => {
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ identifier, password }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.message || "Login failed");

      return data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /* ================= OTP VERIFY ================= */
  const verifyOTP = async (user_id, otp, role) => {
    setLoading(true);
    setError("");

    try {
      const res = await fetch(`${API_BASE}/auth/verify-otp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ user_id, otp, role }),
      });

      const data = await res.json();

      if (!res.ok) throw new Error(data.message);

      // Always use role from server — it's the authoritative value
      const verifiedRole = data.role || role;

      localStorage.setItem("token", data.token);
      localStorage.setItem("role", verifiedRole);
      if (data.name)     localStorage.setItem("name",     data.name);
      if (data.username) localStorage.setItem("username", data.username);
      if (data.email)    localStorage.setItem("email",    data.email);
      if (data.phone)    localStorage.setItem("phone",    data.phone);

      setUser({ role: verifiedRole });

      if (verifiedRole === "parent") {
        await fetchParentDashboard();
      }

      if (verifiedRole === "learner") {
        await fetchLearnerDashboard();
      }

      return verifiedRole;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /* ================= RESEND OTP ================= */
  const resendOTP = async (user_id) => {
    await fetch(`${API_BASE}/auth/resend-otp`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ user_id }),
    });
    alert("OTP resent");
  };

  /* ================= LINK LEARNER ================= */
  const linkLearner = async (learnerCode) => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${API_BASE}/user/parent/link-learner`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ learner_code: learnerCode }),
      });

      return await res.json();
    } catch (err) {
      console.error("Link learner failed");
    }
  };

  const createLearnerWithConsent = async (formData) => {
  const token = localStorage.getItem("token");

  const res = await fetch(`${API_BASE}/user/parent/create-learner`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(formData),
  });

  if (!res.ok) throw new Error("Create learner failed");

  return await res.json();
};

  /* ================= GIVE CONSENT ================= */
const giveConsent = async (learnerId, formData) => {
  const token = localStorage.getItem("token");

  await fetch(`${API_BASE}/user/parent/give-consent`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      learner_id: learnerId,
      ...formData,
    }),
  });
};

  /* ================= FETCH PARENT DASHBOARD ================= */
  const fetchParentDashboard = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${API_BASE}/user/parent/dashboard`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      setParent(data.parent);
      setChildData(data.learners || []);
    } catch (err) {
      console.error("Failed to fetch dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchLearnerReports = async (learnerId) => {
  try {
    const token = localStorage.getItem("token");

    const res = await fetch(
      `${API_BASE}/user/parent/learner/${learnerId}/reports`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    const data = await res.json();

    if (!res.ok) {
      console.error("Reports error:", data);
      throw new Error(data.message || "Reports fetch failed");
    }

    return data;
  } catch (err) {
    console.error(err.message);
    return [];
  }
};

  /* ================= FETCH LEARNER DASHBOARD ================= */
const fetchLearnerDashboard = async (learnerId = null) => {
  console.log("🔥 FETCH CALLED", new Date().toISOString());
  try {

    const token = localStorage.getItem("token");
    const role = localStorage.getItem("role");

    let url;

    if (role === "parent") {
      if (!learnerId) {
        console.error("Missing learnerId for parent");
        return null;
      }
      url = `${API_BASE}/user/parent/learner/${learnerId}/dashboard`;
    } else if (role === "learner") {
      url = `${API_BASE}/user/learner/dashboard`;
    } else {
      console.error("Invalid role for dashboard");
      return null;
    }

    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await res.json();

    if (!res.ok) {
      console.error("Dashboard error:", data);
      throw new Error(data.message || "Failed to fetch dashboard");
    }

    console.log("LEARNER DATA:", data);

    setLearner(data);
    return data;

  } catch (err) {
    console.error("Failed to fetch learner dashboard:", err.message);
    return null;
  } finally {
  }
};

  /* ================= START ASSESSMENT ================= */
  const startAssessment = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/user/assessment/start`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return await res.json();
    } catch (err) {
      console.error("Start assessment failed");
    }
  };

  /* ================= SUBMIT ASSESSMENT ================= */
  const submitAssessment = async (attemptId, answers) => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${API_BASE}/user/assessment/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          attempt_id: attemptId,
          answers: answers,
        }),
      });

      return await res.json();
    } catch (err) {
      console.error("Submit assessment failed");
    }
  };

/* ================= CHECK ASSESSMENT STATUS ================= */
  const checkAssessmentStatus = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(`${API_BASE}/user/assessment/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return await res.json();
    } catch (err) {
      console.error("Assessment status failed");
    }
  };

  const fetchLearnerActivities = async (learnerId) => {
  try {
    const token = localStorage.getItem("token");

    const res = await fetch(
      `${API_BASE}/user/parent/learner/${learnerId}/activities`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    return await res.json();
  } catch (err) {
    console.error(err);
    return [];
  }
};

const fetchLearnerAttendance = async (learnerId) => {
  try {
    const token = localStorage.getItem("token");

    const res = await fetch(
      `${API_BASE}/user/parent/learner/${learnerId}/attendance`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    return await res.json();
  } catch (err) {
    console.error(err);
    return [];
  }
};
/* ================= RECORD VIOLATION ================= */
  const recordViolation = async (attemptId, type) => {
    try {
      const token = localStorage.getItem("token");

      await fetch(`${API_BASE}/user/assessment/violation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
           Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          attempt_id: attemptId,
          violation_type: type,
       }),
     });
    } catch (err) {
      console.error("Violation recording failed");
    }
  };

  /* ================= FETCH EDUCATOR DASHBOARD ================= */
  const fetchEducatorDashboard = async () => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE}/user/educator/dashboard`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await res.json();
      setEducatorData(data);
    
    } catch (err) {
      console.error("Failed to fetch educator dashboard");
    } finally {
      setLoading(false); // ✅ FIX
      }
    };


    const fetchInstitutionDashboard = async () => {
      try {
        const token = localStorage.getItem("token");

        const res = await fetch(
          `${API_BASE}/user/institution/dashboard`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const data = await res.json();
        setInstitutionData(data);

      } catch (err) {
        console.error("Institution fetch failed");
      } finally {
        setLoading(false); // ✅ FIX
        }
      };
  /* ================= LOGOUT ================= */
  const logout = () => {
    ["token","role","name","username","email","phone"].forEach(k => localStorage.removeItem(k));
    setUser(null);
    setParent(null);
    setChildData([]);
    setLearner(null);
  };

  /* ================= RESTORE LOGIN ================= */
 useEffect(() => {
  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");

  if (!token || !role) {
    setLoading(false);
    return;
  }

  // prevent multiple runs
  if (user) return;

  setUser({ role });

  if (role === "parent") {
    fetchParentDashboard();
  } 
  else if (role === "educator") {
    fetchEducatorDashboard();
  } 
  else if (role === "institution") {
    fetchInstitutionDashboard();
  } 
  else {
    setLoading(false);
  }

}, [user]);

  /* ================= RESET PASSWORD ================= */
  const resetPassword = async ({ user_id, otp, new_password }) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id, otp, new_password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Reset failed");
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  /* ================= CHECKOUT ================= */
  const submitCheckout = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/user/learner/checkout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.ok;
    } catch (err) {
      console.error("Checkout failed", err);
      return false;
    }
  };

  /* ================= LEARNER ACTIVITIES (self) ================= */
  const fetchMyActivities = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/user/learner/activities`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return await res.json();
    } catch (err) {
      console.error("Fetch activities failed", err);
      return [];
    }
  };

  /* ================= LEARNER ATTENDANCE (self) ================= */
  const fetchMyAttendance = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/user/learner/attendance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return await res.json();
    } catch (err) {
      console.error("Fetch attendance failed", err);
      return {};
    }
  };

  /* ================= LEARNER NARRATIVE ================= */
  /* ================= REPORT DATA (screen view) ================= */
  const fetchReportData = async (learnerId = null) => {
    try {
      const token = localStorage.getItem("token");
      const url = learnerId
        ? `${API_BASE}/user/parent/learner/${learnerId}/reports/view`
        : `${API_BASE}/user/learner/reports/view`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return null;
      return await res.json();
    } catch (err) {
      console.error("Fetch report data failed", err);
      return null;
    }
  };

  const fetchNarrative = async (learnerId = null) => {
    try {
      const token = localStorage.getItem("token");
      const url = learnerId
        ? `${API_BASE}/user/parent/learner/${learnerId}/narrative`
        : `${API_BASE}/user/learner/narrative`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });

      return await res.json();
    } catch (err) {
      console.error("Fetch narrative failed", err);
      return null;
    }
  };

  /* ================= TEACHER REPORT ================= */
  const fetchTeacherReportData = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/user/educator/report/view`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return null;
      return await res.json();
    } catch (err) {
      console.error("Fetch teacher report failed", err);
      return null;
    }
  };

  /* ================= INSTITUTION REPORT ================= */
  const fetchInstitutionReportData = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/user/institution/report/view`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return null;
      return await res.json();
    } catch (err) {
      console.error("Fetch institution report failed", err);
      return null;
    }
  };

  /* ================= SUBMIT ACTIVITY ================= */
  const submitActivityWork = async (activityId, content) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/user/activity/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ activity_id: activityId, content }),
      });
      return await res.json();
    } catch (err) {
      console.error("Submit activity failed", err);
      return null;
    }
  };

  /* ================= EDUCATOR ACTIVITIES ================= */
  const fetchEducatorActivities = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/user/educator/activities`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return await res.json();
    } catch (err) {
      console.error("Fetch educator activities failed", err);
      return [];
    }
  };

  const createEducatorActivity = async (form) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/user/educator/activity/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      return await res.json();
    } catch (err) {
      console.error("Create activity failed", err);
      return null;
    }
  };

  const fetchActivitySubmissions = async (activityId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/user/educator/activity/${activityId}/submissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return await res.json();
    } catch (err) {
      console.error("Fetch submissions failed", err);
      return [];
    }
  };

  const gradeActivitySubmission = async (submissionId, grade, feedback) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/user/educator/activity/grade`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ submission_id: submissionId, grade, feedback }),
      });
      return await res.json();
    } catch (err) {
      console.error("Grade failed", err);
      return null;
    }
  };

  /* ================= CHECKIN ================= */
  const submitCheckin = async ({ mood, journal }) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/user/learner/checkin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ mood, journal }),
      });
      return res.ok;
    } catch (err) {
      console.error("Checkin failed", err);
      return false;
    }
  };

  /* ================= FORGOT PASSWORD ================= */
  const forgotPassword = async (email) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Request failed");
      return data;
    } catch (err) {
      setError(err.message);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const signup = async (form) => {
  setLoading(true);
  setError("");

  try {
    const res = await fetch(`${API_BASE}/auth/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        ...form,
        role: form.role === "child" ? "learner" : form.role, // ✅ fix mapping
      }),
    });

    const data = await res.json();

    if (!res.ok) throw new Error(data.message || "Signup failed");

    return data;
  } catch (err) {
    setError(err.message);
    return null;
  } finally {
    setLoading(false);
  }
};
  return (
    <AuthContext.Provider
      value={{
        user,
        parent,
        learner,
        childData,
        educatorData,
        createLearnerWithConsent,
        institutionData,
        fetchEducatorDashboard,
        login,
        signup,
        verifyOTP,
        resendOTP,
        logout,
        fetchParentDashboard,
        fetchLearnerDashboard,
        linkLearner,
        giveConsent,
        startAssessment,
        submitAssessment,
        checkAssessmentStatus,
        fetchInstitutionDashboard,
        recordViolation,
        fetchLearnerReports,
        fetchLearnerActivities,
        fetchLearnerAttendance,
        forgotPassword,
        resetPassword,
        submitCheckin,
        submitCheckout,
        fetchMyActivities,
        fetchMyAttendance,
        fetchNarrative,
        fetchReportData,
        submitActivityWork,
        fetchEducatorActivities,
        createEducatorActivity,
        fetchActivitySubmissions,
        gradeActivitySubmission,
        fetchTeacherReportData,
        fetchInstitutionReportData,
        loading,
        error,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);